import { motion } from "framer-motion";
import heroVideo from "../assests/Translation.webm"; // 🎥 your background video

export default function TranslationLocalization() {
  return (
    <div className="bg-gray-50 text-gray-900 font-raleway">
      {/* Hero Section with Video Background */}
      <section className="relative w-full h-[70vh] text-white flex items-center justify-center text-center font-orbitron">
        <video
          src={heroVideo}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 px-6">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-4 tracking-tight"
          >
            Global Communication, Local Impact
          </motion.h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed text-gray-200 font-raleway">
            Translation & Localization services that adapt your products, content,
            and AI systems for diverse languages and cultures worldwide.
          </p>
        </div>
      </section>

      {/* About Section with Stylish Background */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 rounded-2xl shadow-inner my-16">
        <h2 className="text-4xl font-semibold mb-6 font-orbitron tracking-tight">
          Why Translation & Localization Matter
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed font-raleway">
          Expanding globally means more than translating words. It’s about
          capturing the meaning, tone, and cultural context. Whether for AI
          datasets, apps, or marketing campaigns, effective localization ensures
          your message resonates across borders.
        </p>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 font-orbitron tracking-tight">
          Our Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto font-raleway">
          {[
            {
              title: "🌍 Multilingual Translation",
              desc: "Accurate, domain-specific translations in 80+ languages with cultural adaptation.",
            },
            {
              title: "📱 App & Software Localization",
              desc: "UI/UX, documentation, and in-app content tailored for target markets.",
            },
            {
              title: "🎙 Multimedia Localization",
              desc: "Subtitles, dubbing, and voiceovers to connect with diverse audiences.",
            },
            {
              title: "📝 Data Localization",
              desc: "Multilingual training datasets for NLP, speech recognition, and AI models.",
            },
            {
              title: "🔒 Secure & Confidential",
              desc: "Enterprise-grade compliance with strong data protection protocols.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="text-xl font-semibold mb-3 font-orbitron">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6 font-raleway">
        <h2 className="text-4xl font-semibold text-center mb-12 font-orbitron tracking-tight">
          Our Process
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Define goals & target markets",
            "Expert translation & localization",
            "AI-powered tools & automation",
            "Human QA & final delivery",
          ].map((step, idx) => (
            <div key={idx} className="p-4">
              <div className="text-4xl mb-2 font-orbitron">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 px-6 bg-gray-100 font-raleway">
        <h2 className="text-4xl font-semibold text-center mb-12 font-orbitron tracking-tight">
          Applications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            {
              title: "🛒 E-Commerce",
              desc: "Localized product listings, descriptions, and customer reviews.",
            },
            {
              title: "📚 E-Learning",
              desc: "Courses adapted for learners across regions and cultures.",
            },
            {
              title: "🎬 Media & Entertainment",
              desc: "Multilingual subtitles, dubbing, and localized scripts.",
            },
            {
              title: "🏥 Healthcare & Pharma",
              desc: "Accurate translations for patient info, research, and documentation.",
            },
            {
              title: "🤖 AI & NLP",
              desc: "Multilingual datasets for chatbots, speech assistants, and NLP.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="font-semibold mb-2 text-lg font-orbitron">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white font-raleway">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 font-orbitron tracking-tight">
          Go Beyond Translation — Achieve True Localization
        </h2>
        <p className="text-lg max-w-xl mx-auto mb-8">
          Connect with global audiences, build trust, and scale your business
          worldwide with our expert services.
        </p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-white text-indigo-700 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-gray-100 transition"
        >
          Contact Us
        </motion.button>
      </section>
    </div>
  );
}
